import { DEFAULT_INTEREST_RATE } from '../constants/generateCouponsConstants';
import {
  formatCurrency as formatCurrencyShared,
  formatNumber as formatNumberShared,
  formatDate as formatDateDisplay,
} from '../../../utils/formatUtils';

/**
 * Calcula el interés entre dos fechas
 * @param {number} principal - Monto principal
 * @param {Date} startDate - Fecha inicial
 * @param {Date} endDate - Fecha final
 * @param {number} rate - Tasa de interés (por defecto 9% anual)
 * @returns {Object} - Objeto con interest y total
 */
export const calculateInterest = (principal, startDate, endDate, rate = DEFAULT_INTEREST_RATE) => {
  const diffTime = Math.abs(endDate - startDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const total = principal * (1 + (rate * diffDays) / 30);
  const interest = total - principal;

  return {
    interest: Math.ceil(interest).toFixed(2),
    total: Math.ceil(total).toFixed(2),
  };
};

/**
 * Valida entrada numérica para campos
 * @param {string} value - Valor a validar
 * @returns {boolean} - True si es válido
 */
export const validateNumericInput = (value) => {
  return /^\d*\.?\d*$/.test(value) || value === '.' || value === '0.' || value === '';
};

/**
 * Extrae mensaje de error de diferentes fuentes
 * @param {Object} error - Error object
 * @returns {string} - Mensaje de error legible
 */
export const getErrorMessage = (error) => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.payload?.message) {
    return error.payload.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'Ocurrió un error inesperado. Por favor intente nuevamente.';
};

/**
 * Formatea fecha para input de tipo date
 * @param {Date} date - Fecha a formatear
 * @returns {string} - Fecha en formato YYYY-MM-DD
 */
export const formatDateForInput = (date) => {
  if (!date || !(date instanceof Date)) {
    return new Date().toISOString().substring(0, 10);
  }
  return date.toISOString().substring(0, 10);
};

/**
 * Crea fechas iniciales para el formulario
 * @returns {Object} - Objeto con fechas iniciales
 */
export const createInitialDates = () => {
  const now = new Date();
  const oneWeekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const twoWeeksLater = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

  return {
    due1: now,
    due2: oneWeekLater,
    due3: twoWeeksLater,
  };
};

/**
 * Valida si un método de pago es especial basado en su código
 * @param {string} methodCode - Código del método de pago
 * @returns {boolean} - True si es especial
 */
export const isSpecialPaymentMethod = (methodCode) => {
  const specialCodes = ['BA2014', 'BD2001'];
  return specialCodes.includes(methodCode);
};

/**
 * Obtiene el código del método de pago por UUID
 * @param {Array} paymentMethods - Array de métodos de pago
 * @param {string} uuid - UUID del método de pago
 * @returns {string|null} - Código del método o null
 */
export const getPaymentMethodCode = (paymentMethods, uuid) => {
  const method = paymentMethods.find(method => method.uuid === uuid);
  return method ? method.code : null;
};

/**
 * Verifica si un método de pago requiere contratos (no especiales)
 * @param {Array} paymentMethods - Array de métodos de pago
 * @param {string} uuid - UUID del método de pago
 * @returns {boolean} - True si requiere contrato
 */
export const requiresContract = (paymentMethods, uuid) => {
  const code = getPaymentMethodCode(paymentMethods, uuid);
  return !isSpecialPaymentMethod(code);
};

/**
 * Calcula valores de diferimiento
 * @param {Object} amounts - Montos originales
 * @param {number} percentage - Porcentaje de diferimiento
 * @returns {Object} - Valores diferidos
 */
export const calculateDefermentValues = (amounts, percentage) => {
  if (!amounts || typeof percentage !== 'number') {
    return {
      diff1: '0.00',
      diff2: '0.00',
      diff3: '0.00',
    };
  }

  const diff1 = amounts.due1 - (amounts.due1 * percentage) / 100;
  const diff2 = amounts.due2 - (amounts.due2 * percentage) / 100;
  const diff3 = amounts.due3 - (amounts.due3 * percentage) / 100;

  return {
    diff1: Math.round(diff1 * 100) / 100,
    diff2: Math.round(diff2 * 100) / 100,
    diff3: Math.round(diff3 * 100) / 100,
  };
};

/**
 * Normaliza valores numéricos de entrada
 * @param {any} value - Valor a normalizar
 * @returns {number} - Valor numérico normalizado
 */
export const normalizeNumericValue = (value) => {
  if (value === '' || value === null || value === undefined) {
    return 0;
  }
  
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Convierte fecha a string para API
 * @param {Date} date - Fecha a convertir
 * @returns {string} - Fecha en formato YYYY-MM-DD
 */
export const dateToApiString = (date) => {
  if (!date || !(date instanceof Date)) {
    return new Date().toISOString().split('T')[0];
  }
  return date.toISOString().split('T')[0];
};

/**
 * Construye objeto de datos para la API
 * @param {Object} formData - Datos del formulario
 * @param {Object} dueDates - Fechas de vencimiento
 * @param {Object} dueDatesCalendar - Fechas del calendario
 * @param {Array} excludedContracts - Contratos excluidos
 * @returns {Object} - Objeto preparado para la API
 */
export const buildCouponApiData = (formData, dueDates, dueDatesCalendar, excludedContracts = []) => {
  return {
    uuid_plan: formData.plan,
    uuid_group: formData.group === 'all' ? null : formData.group,
    uuid_payment_method: formData.paymentMethod,
    contract_number: formData.contract || null,
    defermentPercentage: normalizeNumericValue(formData.defermentPercentage),
    coefficient: normalizeNumericValue(formData.coefficient),
    first_due_date: dateToApiString(dueDatesCalendar.due1),
    second_due_date: dateToApiString(dueDatesCalendar.due2),
    third_due_date: dateToApiString(dueDatesCalendar.due3),
    first_due_amount: normalizeNumericValue(dueDates.due1),
    second_due_amount: normalizeNumericValue(dueDates.due2),
    third_due_amount: normalizeNumericValue(dueDates.due3),
    excluded_contracts: excludedContracts,
  };
};

/**
 * Valida los datos del formulario antes del envío
 * @param {Object} formData - Datos del formulario
 * @param {Object} dueDates - Montos de vencimiento
 * @param {Array} paymentMethods - Métodos de pago
 * @returns {Object} - Resultado de validación { isValid, errors }
 */
export const validateFormData = (formData, dueDates, paymentMethods = []) => {
  const errors = [];
  
  if (!formData.plan) {
    errors.push('Debe seleccionar un plan');
  }
  
  if (!formData.paymentMethod) {
    errors.push('Debe seleccionar una forma de pago');
  }
  
  if (requiresContract(paymentMethods, formData.paymentMethod)) {
    if (!formData.contract || normalizeNumericValue(formData.contract) < 1) {
      errors.push('Debe introducir un número de contrato válido');
    }
  }
  
  if (normalizeNumericValue(dueDates.due1) <= 0) {
    errors.push('El primer vencimiento debe ser mayor a 0');
  }
  
  if (normalizeNumericValue(formData.coefficient) < 0) {
    errors.push('El coeficiente debe ser mayor o igual a 0');
  }
  
  const deferment = normalizeNumericValue(formData.defermentPercentage);
  if (deferment < 0 || deferment > 100) {
    errors.push('El porcentaje de diferimiento debe estar entre 0 y 100');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Formatea montos para mostrar en la UI
 * @param {number} amount - Monto a formatear
 * @param {string} currency - Símbolo de moneda
 * @returns {string} - Monto formateado
 */
export const formatCurrency = (amount, currency = '') => {
  const formatted = formatCurrencyShared(normalizeNumericValue(amount));
  if (!currency) {
    return formatted;
  }
  return `${currency}${formatted}`;
};

/**
 * Debounce para optimizar performance en inputs
 * @param {Function} func - Función a ejecutar
 * @param {number} wait - Tiempo de espera en ms
 * @returns {Function} - Función debounced
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Convierte string de fecha a objeto Date
 * @param {string} dateString - Fecha en string
 * @returns {Date} - Objeto Date
 */
export const stringToDate = (dateString) => {
  if (!dateString) return new Date();
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? new Date() : date;
};

/**
 * Calcula la diferencia en días entre dos fechas
 * @param {Date} startDate - Fecha inicial
 * @param {Date} endDate - Fecha final
 * @returns {number} - Diferencia en días
 */
export const calculateDaysDifference = (startDate, endDate) => {
  const diffTime = Math.abs(endDate - startDate);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Transforma los datos de cupones para su visualización en la tabla
 * @param {Array} coupons - Array de cupones desde la API
 * @returns {Array} - Array de cupones transformados
 */
export const transformCouponsData = (coupons) => {
  return coupons.map((coupon) => {
    const feeTypeName = coupon.is_score
      ? 'Puntaje'
      : coupon.is_advance_payment
      ? 'Adelanto'
      : Number(coupon.fee_number1) === 0
      ? 'Adecuación'
      : `${coupon.fee_number1} ${
          coupon.fee_number2 === 0 ||
          coupon.fee_number2 === null ||
          coupon.fee_number2 === undefined
            ? ''
            : ` - ${coupon.fee_number2}`
        }`;

    const cuotas = feeTypeName;

    let importes;
    if (feeTypeName === 'Adjudicación' || feeTypeName === 'Adelanto') {
      const formattedDate = formatDateDisplay(coupon.first_due_date);
      importes = [`Venc: ${formattedDate} $${Number(coupon.first_due_amount)}`];
    } else {
      importes = [
        `1 - Venc: ${formatDateDisplay(coupon.first_due_date)} $${Number(coupon.first_due_amount)}`,
        `2 - Venc: ${formatDateDisplay(coupon.second_due_date)} $${Number(
          coupon.second_due_amount
        )}`,
        `3 - Venc: ${formatDateDisplay(coupon.third_due_date)} $${Number(coupon.third_due_amount)}`,
      ];
    }

    return {
      uuid: coupon.uuid,
      orden: coupon.contract_number,
      suscriptores: `- ${coupon.subscribers[0].subscriber.last_name}, ${coupon.subscribers[0].subscriber.first_name}`,
      mes: formatDateDisplay(coupon.first_due_date, { options: { month: 'long' }, fallback: '' }),
      año: coupon.year,
      cuotas: cuotas,
      importes: importes,
      cancelado:
        (coupon.cancellation_date === '0000-00-00 00:00:00') | !coupon.cancellation_date
          ? 'No'
          : `Sí - ${formatDateDisplay(coupon.cancellation_date, { fallback: '' })}`,
      can_delete: coupon.can_delete,
    };
  });
};

/**
 * Normaliza un valor a número (alias para mantener compatibilidad)
 * @param {any} value - Valor a normalizar
 * @returns {number} - Valor normalizado
 */
export const normalizeValue = (value) => {
  return normalizeNumericValue(value);
};

/**
 * Genera elementos de menú para años
 * @param {number} startYear - Año inicial (opcional, por defecto 1990)
 * @returns {Array} - Array de elementos MenuItem
 */
export const generateYearMenuItems = (startYear = 1990) => {
  const currentYear = new Date().getFullYear();
  let items = [];

  for (let i = currentYear; i >= startYear; i--) {
    items.push({
      key: i,
      value: i,
      label: i.toString(),
    });
  }

  return items;
};

/**
 * Convierte array de años a elementos de menú
 * @param {number} startYear - Año inicial
 * @param {number} endYear - Año final (opcional, por defecto año actual)
 * @returns {Array} - Array de elementos para select
 */
export const generateYearOptions = (startYear = 1990, endYear = new Date().getFullYear()) => {
  const years = [];
  for (let year = endYear; year >= startYear; year--) {
    years.push(year);
  }
  return years;
};

/**
 * Valida si una fecha es válida
 * @param {any} date - Fecha a validar
 * @returns {boolean} - True si es válida
 */
export const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Calcula el total de días entre fechas
 * @param {Date} start - Fecha inicial
 * @param {Date} end - Fecha final
 * @returns {number} - Número de días
 */
export const getDaysBetweenDates = (start, end) => {
  if (!isValidDate(start) || !isValidDate(end)) return 0;
  return calculateDaysDifference(start, end);
};

/**
 * Formatea número con separadores de miles
 * @param {number} number - Número a formatear
 * @returns {string} - Número formateado
 */
export const formatNumber = (number) => {
  return formatNumberShared(normalizeNumericValue(number), { decimals: 0 });
};

/**
 * Redondea número a decimales específicos
 * @param {number} number - Número a redondear
 * @param {number} decimals - Cantidad de decimales
 * @returns {number} - Número redondeado
 */
export const roundToDecimals = (number, decimals = 2) => {
  const normalized = normalizeNumericValue(number);
  return Math.round(normalized * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

/**
 * Convierte timestamp a fecha legible
 * @param {number} timestamp - Timestamp en milisegundos
 * @returns {string} - Fecha formateada
 */
export const timestampToDate = (timestamp) => {
  const date = new Date(timestamp);
  return isValidDate(date) ? formatDateForInput(date) : '';
};

/**
 * Obtiene el primer día del mes
 * @param {Date} date - Fecha de referencia
 * @returns {Date} - Primer día del mes
 */
export const getFirstDayOfMonth = (date = new Date()) => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Obtiene el último día del mes
 * @param {Date} date - Fecha de referencia
 * @returns {Date} - Último día del mes
 */
export const getLastDayOfMonth = (date = new Date()) => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

/**
 * Suma días a una fecha
 * @param {Date} date - Fecha base
 * @param {number} days - Días a sumar
 * @returns {Date} - Nueva fecha
 */
export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Resta días a una fecha
 * @param {Date} date - Fecha base
 * @param {number} days - Días a restar
 * @returns {Date} - Nueva fecha
 */
export const subtractDays = (date, days) => {
  return addDays(date, -days);
};
